import { useApolloClient } from '@apollo/client/react';
import { GraphQLError } from 'graphql/error';

import { SPAN_SUBMIT_APPLICATION } from '@constants/otel';
import {
  AddAttributionDetailsInput,
  DirectDebit,
  SetApplicationMutation,
} from '@generated/gql/graphql';
import { useAttribution } from '@hooks/useAttribution';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useTrace } from '@services/otel';

import { addPaymentMethod, setApplication } from './query';
import type { SubmitBody } from './types';

const useSubmitApplication = () => {
  const { withTrace } = useTrace();
  const { basketId } = useBasketContext();
  const client = useApolloClient();
  const { addAttributionDetails } = useAttribution();

  const executeSubmitApplication = async (
    bankToken: string,
    applicationInput: SubmitBody,
    attributionInput: Nullable<AddAttributionDetailsInput>
  ) => {
    try {
      if (attributionInput) {
        await addAttributionDetails(basketId, attributionInput);
      }

      // addPaymentMethod needs to be called before setApplication,
      // if addPaymentDetails fails then set application should not be called as this can't be called again on the same basket.
      const addPaymentMethodResponse = await client.mutate({
        mutation: addPaymentMethod,
        variables: {
          paymentMethod: {
            basketId,
            paymentMethod: {
              billingAddress: applicationInput.directDebit.billingAddress,
              directDebit: {
                bankAccountToken: bankToken,
              } as DirectDebit,
            },
          },
        },
      });

      if (addPaymentMethodResponse.error) {
        return Promise.reject(addPaymentMethodResponse.error);
      }
    } catch (error) {
      return Promise.reject([error as GraphQLError]);
    }

    return client.mutate<SetApplicationMutation>({
      mutation: setApplication,
      variables: {
        basketId,
        accountHolders: applicationInput.accountHolders,
        recentlyMovedIn: applicationInput.recentlyMovedIn,
        correspondenceAddress: applicationInput.directDebit.billingAddress,
      },
    });
  };

  return (
    bankToken: string,
    applicationInput: SubmitBody,
    attributionInput: Nullable<AddAttributionDetailsInput>
  ) =>
    withTrace(SPAN_SUBMIT_APPLICATION, () =>
      executeSubmitApplication(bankToken, applicationInput, attributionInput)
    );
};

export { useSubmitApplication };
